// What a bot player does on its turn, as a pure function so it can be tested without
// a DOM and without a lobby. The driver in gameController.js calls chooseBotMove()
// over and over until it answers null, then ends the turn.

import {defaultAdjustedCost} from "./turnRules.js";

// The bot's reading of the card library. The cards keep their numbers inside their
// play() closures, where nothing can read them back, so the bot needs its own copy —
// and a copy drifts. test/bot-policy.test.cjs plays every card against stub mutators
// and fails if an entry here stops describing the card it names, so adding a card
// means adding a line here too.
//
// Only the unconditional part of a card is described. Lime-Mocktail's heal and Sista
// Droppens bonus depend on how much everyone has drunk; the bot plans around the
// damage it can count on and takes the rest as a bonus. `variable` marks a number that
// is only a typical value (Kombucha's roll); the phone does not print those.
export const CARD_POWER = {
  "zero-lager": { damage: 3 },
  "lime-mocktail": { damage: 4 },
  bubbelvatten: { damage: 2, aoe: true },
  "virgin-colada": { heal: 4 },
  gurkvatten: { shield: "minion" },
  "espresso-tonic": { damage: 3 },
  "jordgubbs-fizz": { damage: 6 },
  isvatten: { heal: 1 },
  ingefara: { damage: 5, pierce: true },
  kombucha: { damage: 3, variable: true },
  citronpress: { damage: 2 },
  "sista-droppen": { damage: 7 },
  barhandduk: { shield: "minion" },
  spegelglas: { shield: "reflect" },
  kapsyl: { shield: "minion" },
  kaktus: { shield: "minion" },
  happyhour: { shield: "minion" },
  partytalt: { shield: "minion" },
  // The 2 mot 2 cards, valued with a partner still standing — which is the only
  // situation a bot ever holds one, since the standard deck has them only in team matches.
  // Only what lands on the bot itself counts: the healer and the defender plan for the
  // bot's own glass, so a partner's heal or wall is nothing they could act on.
  lagskal: { heal: 3 },
  ryggstod: {},
  passning: {},
  dubbelstot: { damage: 7 },
  uppoffring: { recoil: 2 },
  // Bots never draw these — they play the standard deck — but Bytt är bytt can hand
  // them over. Tricks, and damage that depends entirely on the board (Shotroulette,
  // Konfettikanonen, Glaskrossen), carry nothing the bot can plan around, so it never
  // plays them. `recoil` is what a card makes its own player drink; `leech` marks a
  // minion planted on an enemy, which must never be mistaken for a wall of your own.
  skal: { damage: 2, aoe: true, recoil: 2 },
  dubbelshot: { damage: 4 },
  shotroulette: {},
  ficktjuven: {},
  patar: {},
  "bytt-ar-bytt": {},
  dricks: {},
  kreditkortet: {},
  "dela-notan": {},
  vattenkaraffen: { heal: 2 },
  bakfyllan: { damage: 2, variable: true },
  paraplydrinken: { shield: "dodge" },
  bartendern: { shield: "minion" },
  smallkaramellen: { shield: "minion" },
  hembryggaren: { shield: "minion" },
  pubrundan: { damage: 1, aoe: true, variable: true },
  konfettikanonen: {},
  glaskrossen: {},
  utslagsvasken: {},
  stangningsdags: { damage: 1, aoe: true, variable: true },
  snyltgasten: { leech: true },
  // The second wave. Conditional halves (Lättöl's draw, Sockerchocken's follow-up,
  // Livlinan) are bonuses the bot does not plan for, like Lime-Mocktail's heal. The
  // shields that are not minions — armor, rebound, counter, lifeline — are named so the
  // builder files them, but the bot only knows what to do with a mirror.
  lattol: { damage: 2 },
  sugroret: { damage: 3 },
  kanonbollen: { damage: 9 },
  sockerchocken: { damage: 2 },
  langdrinken: { damage: 2, variable: true },
  hamnden: {},
  champagneduschen: { damage: 3, aoe: true },
  "rundan-pa-mig": { damage: 3, aoe: true, recoil: 3 },
  // Two of the opponents, not all of them: the aoe reading over-counts a big table.
  spillet: { damage: 2, aoe: true, variable: true },
  bordsdansen: { damage: 2, aoe: true, recoil: 2 },
  skumpartyt: {},
  salvan: { damage: 1, aoe: true, pierce: true },
  vattenflaskan: { heal: 2 },
  sportdrycken: { heal: 3 },
  "kaffe-macka": { heal: 5 },
  frukostbuffen: { heal: 8 },
  "andra-andningen": {},
  promenaden: { heal: 4 },
  pappmuggen: { shield: "minion" },
  olbacken: { shield: "minion" },
  underlaggsstapeln: { shield: "minion" },
  kylvaskan: { shield: "minion" },
  isskulpturen: { shield: "minion" },
  dorrvakten: { shield: "minion" },
  glasunderlagget: { shield: "armor" },
  djn: { shield: "minion" },
  shottornet: { shield: "minion" },
  kassoren: { shield: "minion" },
  partyhatten: { shield: "minion" },
  fotografen: { shield: "minion" },
  fyllekompisen: { leech: true },
  barkortet: {},
  pafyllningen: {},
  "spill-pa-golvet": {},
  slutsalt: {},
  "samma-igen": {},
  kontanter: {},
  dricksburken: {},
  notan: {},
  "fri-bar": {},
  kontraskalen: { shield: "counter" },
  tvavagsspegeln: { shield: "rebound" },
  livlinan: { shield: "lifeline" },
  tarningen: { damage: 3, variable: true },
  // Tails is what the drift test's stub lands on, and it is the only outcome the bot
  // could count on: a card that may only hurt you is not one it plays.
  myntkastet: { recoil: 2 },
  stolleken: {},
  lyckohjulet: {},
  "sista-skalen": {},
  "high-five": {},
  ryggdunken: {},
  tagteam: {},
  lagkaptenen: {},
  // The drift test's stub rolls 3: the bottle lands on the third seat, which is neither
  // the bot nor its target, and the duel is a tie, which costs the target two.
  "snurra-flaskan": {},
  tarningsduellen: { damage: 2, variable: true },
  // The mini-games: a bot takes part in one when a person starts it (the host plays its
  // hand and its vote), but never starts one itself.
  blackjack: {},
  utrostningen: {},
};

// Sips at which the bot stops pressuring and starts topping itself up. Low enough to
// leave room for one more big hit before 30, high enough that it does not waste early
// turns healing 1.
const HEAL_THRESHOLD = 22;
// A sip actually taken is worth more than a sip a minion soaks, but soaking is worth
// something: without this the bot sits on its mana while a 9 HP wall never comes down.
const SIP_WEIGHT = 2;

function minionHp(player) {
  return (player?.minions || []).reduce((total, minion) => total + Math.max(0, minion.hp), 0);
}

// What the target would actually drink, after their minions have eaten what they can.
export function effectiveDamage(stats, target) {
  if (!stats?.damage) return 0;
  if (stats.pierce) return stats.damage;
  return Math.max(0, stats.damage - minionHp(target));
}

function move(card, target = null) {
  return { cardId: card.id, targetId: target?.id ?? null };
}

function wouldFinish(stats, enemy) {
  return enemy.sipsTaken + effectiveDamage(stats, enemy) >= enemy.maxSips;
}

// Cheapest first: two cards that both finish the same player are worth the same, so
// keep the mana for whatever comes after.
function finisher(options, living) {
  for (const { card, stats } of options) {
    if (!stats.damage) continue;
    const victim = living.find((enemy) => wouldFinish(stats, enemy));
    if (victim) return move(card, stats.aoe ? null : victim);
  }
  return null;
}

function healer(self, options) {
  if (self.sipsTaken < HEAL_THRESHOLD) return null;
  const best = options
    .filter(({ stats }) => stats.heal > 0)
    .sort((left, right) => right.stats.heal - left.stats.heal)[0];
  return best ? move(best.card) : null;
}

function defender(self, options, living) {
  if (minionHp(self) > 0 || !self.sipsTaken || !living.length) return null;
  const wall = options.find(({ stats }) => stats.shield === "minion");
  return wall ? move(wall.card) : null;
}

// Whoever has drunk least is both the hardest to finish and the one currently winning,
// since fewest sips takes the match. That makes them the right target.
function pressure(options, living) {
  if (!living.length) return null;
  const focus = [...living].sort((left, right) => left.sipsTaken - right.sipsTaken)[0];

  let best = null;
  for (const option of options) {
    if (!option.stats.damage) continue;
    const targets = option.stats.aoe ? living : [focus];
    const value = targets.reduce((total, enemy) => {
      const through = Math.min(effectiveDamage(option.stats, enemy), enemy.maxSips - enemy.sipsTaken);
      const soaked = Math.min(option.stats.damage, minionHp(enemy));
      return total + through * SIP_WEIGHT + soaked;
    }, 0);
    if (value > 0 && (!best || value > best.value)) best = { option, value };
  }
  if (!best) return null;
  return move(best.option.card, best.option.stats.aoe ? null : focus);
}

// Mana that would otherwise be thrown away at the end of the turn.
function filler(self, options) {
  const mirror = options.find(({ stats }) => stats.shield === "reflect");
  if (mirror && !self.reflect) return move(mirror.card);
  const heal = options.find(({ stats }) => stats.heal > 0);
  if (heal && self.sipsTaken > 0) return move(heal.card);
  return null;
}

// Skål! makes its own player drink too. A bot that close to the edge keeps it in hand.
function knocksSelfOut(self, stats) {
  return self.sipsTaken + (stats.recoil || 0) >= self.maxSips;
}

// One card, or null when the bot should end its turn. The caller plays the card and
// asks again, so a whole turn is this function called until it says no.
export function chooseBotMove({
  self,
  enemies = [],
  cardById,
  adjustedCost = defaultAdjustedCost,
  power = CARD_POWER,
} = {}) {
  if (!self || !Array.isArray(self.hand)) return null;

  const options = self.hand
    .map((cardId) => ({ card: cardById(cardId), stats: power[cardId] }))
    .filter(({ card, stats }) => Boolean(card && stats) && self.mana >= adjustedCost(self, card) && !knocksSelfOut(self, stats))
    .sort((left, right) => adjustedCost(self, left.card) - adjustedCost(self, right.card));
  const living = enemies.filter((enemy) => enemy && enemy.sipsTaken < enemy.maxSips);

  return (
    finisher(options, living)
    || healer(self, options)
    || defender(self, options, living)
    || pressure(options, living)
    || filler(self, options)
  );
}
